import { useEffect, useMemo, useRef, useState } from 'react';

import type { AiAction } from '~/components/AiChat/types';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import { fetchData } from '~/components/useDataFetcher/utils';
import type { ClaimActionHistoryResponse } from '~/server_shared/openapi/data-contracts';

import type { ActionVersionsRead } from '../ActionHistory/utils';
import { getSupersededEntries, instantOf } from '../ActionHistory/utils';
import { getReferencedActionId } from '../utils';

/** For each action with an entry superseded, the timestamp of the entry that superseded it. */
const supersedingTimestamps = (notifications: StpNotification[], actions: AiAction[]): Map<string, string> => {
  const timestamps = new Map<string, string>();
  getSupersededEntries(notifications, actions).forEach((superseding, older) => {
    const actionId = getReferencedActionId(older);
    if (actionId) timestamps.set(actionId, superseding.timestamp);
  });

  return timestamps;
};

/** The reads held, and the claim they were read for. */
interface ClaimReads {
  claimId: number;
  byAction: Record<string, ActionVersionsRead>;
}

const NO_READS: Record<string, ActionVersionsRead> = {};

/**
 * The history of each action whose log holds an entry from before Clive last revised it, by action id,
 * for that entry to be drawn from the version it was about.
 *
 * The first page the route serves, read once for the action's last entry and again when a later one
 * arrives: a read holds the version of every entry up to the one it was read for. Nothing is read for an
 * action never revised, or logged once. No error dialog, as the history dialog has none: the entries
 * fall back to what they said. A read that fails is forgotten and asked again when either source next
 * changes, never off its own failure, so a route that is down is not read in a loop.
 *
 * The hub stays mounted when the claim changes, so the reads are the claim's: those of the claim shown
 * before are dropped, and one of its reads landing after the change lands on nothing.
 */
export const useSupersededActionVersions = (
  claimId: number,
  notifications: StpNotification[],
  actions: AiAction[]
): Record<string, ActionVersionsRead> => {
  const [reads, setReads] = useState<ClaimReads>({ claimId, byAction: {} });
  // Each read sent and not failed, by action and the entry it was read for, so none is sent twice.
  const requested = useRef(new Set<string>());
  const shownClaimId = useRef(claimId);

  // Before the reads below, so the claim's first reads are not skipped for keys the one before sent.
  useEffect(() => {
    if (shownClaimId.current === claimId) return;
    shownClaimId.current = claimId;
    requested.current.clear();
  }, [claimId]);

  const wanted = useMemo(() => supersedingTimestamps(notifications, actions), [notifications, actions]);

  useEffect(() => {
    wanted.forEach((coversThrough, actionId) => {
      const key = `${actionId}@${coversThrough}`;
      if (requested.current.has(key)) return;
      requested.current.add(key);

      void fetchData<ClaimActionHistoryResponse>(
        `/api/v1/claims/${claimId}/actions/${actionId}/history`,
        undefined,
        true,
        false
      ).then((result) => {
        if (shownClaimId.current !== claimId) return;
        if (result.type !== 'FETCH_SUCCESS') {
          requested.current.delete(key);
          return;
        }

        setReads((previous) => {
          const byAction = previous.claimId === claimId ? previous.byAction : {};
          const held = byAction[actionId];
          // A read sent before the one held, landing after it, holds no more than it does.
          if (held && instantOf(held.coversThrough) > instantOf(coversThrough)) return previous;

          return {
            claimId,
            byAction: { ...byAction, [actionId]: { versions: result.payload.versions, coversThrough } },
          };
        });
      });
    });
  }, [claimId, wanted]);

  // Until the new claim's first read lands, the state still holds the reads of the one before.
  return reads.claimId === claimId ? reads.byAction : NO_READS;
};
